import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    PointElement
);

const DashChart = ({ color = '#3b82f6', rows, chartType = 'bar' }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [chartData, setChartData] = useState(null);
    const [hasData, setHasData] = useState(false); // Track if data exists

    useEffect(() => {
        if (!rows) {
            setIsLoading(false);
            return;
        }

        const countsByMonth = rows.reduce((acc, record) => {
            const month = new Date(record.createdAt).getMonth();
            acc[month]++;
            return acc;
        }, Array(12).fill(0));

        // Check if there is any data to show
        const totalCount = countsByMonth.reduce((sum, count) => sum + count, 0);
        setHasData(totalCount > 0);

        const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'];
        const chartDataset = {
            label: 'Requests',
            data: countsByMonth.slice(0, 7),
            backgroundColor: color,
            borderColor: color,
            borderWidth: 2,
            fill: chartType === 'line',
            tension: 0.3,
            borderRadius: chartType === 'bar' ? 4 : 0, // Adds slightly rounded tops to bar charts
        };

        setChartData({ labels, datasets: [chartDataset] });
        setIsLoading(false);
    }, [rows, color, chartType]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false // FIX: Removes the "Requests" label
            },
            tooltip: {
                backgroundColor: '#ffffff',
                titleColor: '#000',
                bodyColor: '#000',
                borderColor: color,
                borderWidth: 1,
            },
        },
        scales: chartType === 'doughnut' ? {} : {
            x: {
                grid: { display: false },
                ticks: {
                    color: '#6b7280',
                    font: { weight: '500' },
                },
            },
            y: {
                beginAtZero: true,
                grid: { drawBorder: false, color: 'rgba(0,0,0,0.05)' },
                ticks: {
                    color: '#6b7280',
                    font: { weight: '500' },
                    precision: 0, // FIX: Forces integers only (no decimals)
                    stepSize: 1   // FIX: Steps by exactly 1
                },
            },
        },
        layout: { padding: 10 },
    };

    const ChartComponent = chartType === 'line' ? Line : chartType === 'doughnut' ? Doughnut : Bar;

    return (
        <div style={{
            height: '150px',
            width: '100%',
            position: 'relative',
            backgroundColor: isLoading ? 'rgba(243, 244, 246, 0.1)' : 'transparent',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {isLoading ? (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <div style={{
                        width: '24px',
                        height: '24px',
                        border: `3px solid ${color}20`,
                        borderTop: `3px solid ${color}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                    }} />
                    <span style={{ color: '#6b7280', fontSize: '12px', fontWeight: '500' }}>
                        Loading data...
                    </span>
                </div>
            ) : !hasData ? (
                /* FIX: Premium empty state when there are 0 records */
                <div style={{ textAlign: 'center', color: '#9ca3af', fontFamily: 'sans-serif' }}>
                    <svg style={{ width: '32px', height: '32px', margin: '0 auto 8px', opacity: 0.5 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <span style={{ fontSize: '14px', fontWeight: '500', display: 'block', color: '#6b7280' }}>No activity yet</span>
                    <span style={{ fontSize: '12px' }}>Data will appear once available</span>
                </div>
            ) : (
                chartData && <ChartComponent data={chartData} options={options} />
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default DashChart;